<template>
  <component
    :is="getComponentType()"
    v-bind="getComponentProps()"
    class="group block w-full cursor-pointer text-left transition-all duration-150 ease-in-out"
    :class="[
      'py-' + (size === 'large' ? '4' : '3') + ' px-4',
      isSelected
        ? 'bg-primary/10 border-primary dark:border-primary dark:bg-primary/10 border-l-4 shadow-sm'
        : 'hover:bg-lightSecondaryColor dark:hover:bg-darkSecondaryColor border-l-4 border-transparent',
    ]"
    @click="handleClick"
    @mouseenter="$emit('hover')"
    @mousemove="$emit('hover')"
  >
    <div class="flex items-stretch gap-4">
      <div class="flex flex-shrink-0 items-center">
        <div
          class="flex items-center justify-center rounded-lg transition-all duration-150 ease-in-out"
          :class="[
            size === 'large' ? 'h-10 w-10' : 'h-8 w-8',
            isSelected
              ? 'bg-primary/20 dark:bg-primary/20 text-primary dark:text-primary'
              : 'bg-lightSecondaryColor text-lightPrimaryTextColor group-hover:bg-lightTertiaryColor dark:bg-darkSecondaryColor dark:text-darkPrimaryTextColor dark:group-hover:bg-darkTertiaryColor',
          ]"
        >
          <BaseIcon :icon="item.icon" :class="size === 'large' ? 'h-5 w-5' : 'h-4 w-4'" />
        </div>
      </div>

      <div class="min-w-0 flex-1">
        <div
          class="truncate text-sm transition-colors duration-150 ease-in-out"
          :class="[
            size === 'large' ? 'font-semibold' : 'font-medium',
            isSelected
              ? 'text-lightPrimaryTextColor dark:text-darkPrimaryTextColor'
              : 'text-lightPrimaryTextColor dark:text-darkPrimaryTextColor',
          ]"
        >
          {{ item.title }}
        </div>
        <div
          class="mt-0.5 truncate text-xs transition-colors duration-150 ease-in-out"
          :class="
            isSelected
              ? 'text-lightPrimaryTextColor dark:text-darkPrimaryTextColor'
              : 'text-lightSecondaryTextColor dark:text-darkSecondaryTextColor'
          "
        >
          {{ item.subtitle }}
        </div>

        <div v-if="shouldShowSectionsBadge" class="mt-2 sm:hidden">
          <QuickAccessItemSectionsBadge :section-name="item.sectionName" :is-selected="isSelected" />
        </div>
      </div>

      <!-- Right Column: Badge (desktop only) -->
      <div v-if="shouldShowSectionsBadge" class="hidden flex-shrink-0 items-center sm:flex">
        <QuickAccessItemSectionsBadge :section-name="item.sectionName" :is-selected="isSelected" />
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import BaseIcon from "../components/BaseIcon.vue";
import { MenuItemType } from "../dashboard/enum.menuItemType";
import { isExternalLink } from "../util/isExternalLink";
import QuickAccessItemSectionsBadge from "./QuickAccessItemSectionsBadge.vue";
import type { QuickAccessItem } from "./model.quickAccess";

interface Props {
  item: QuickAccessItem;
  isSelected: boolean;
  size?: "large" | "small";
  shouldShowSectionsBadge?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: "small",
  shouldShowSectionsBadge: false,
});

const emit = defineEmits<{
  execute: [item: QuickAccessItem];
  hover: [];
}>();

function getComponentType() {
  if (props.item.navigationMetadata.type === MenuItemType.ROUTE) {
    return isExternalLink(props.item.navigationMetadata.to) ? "a" : "RouterLink";
  }
  return "button";
}

function getComponentProps() {
  if (props.item.navigationMetadata.type === MenuItemType.ROUTE) {
    if (isExternalLink(props.item.navigationMetadata.to)) {
      return { href: props.item.navigationMetadata.to };
    } else {
      return { to: props.item.navigationMetadata.to };
    }
  }
  return {};
}

function handleClick() {
  emit("execute", props.item);
}
</script>
